import { all, put, select } from "redux-saga/effects";

import { topicsApiSlice } from "api/topicsApi.slice";
import type { ImageCollection } from "common/types/imageCollection";
import { apiFetch } from "common/utilities";
import { dataUrlToBlob } from "common/utilities/dataUrlToBlob";
import { isObject } from "common/utilities/isObject";
import { RequestError } from "common/utilities/requestError";
import { trackSaga } from "common/utilities/trackSaga";
import { uploadMapFailure, uploadMapSuccessful } from "domains/createMapDetail/actions";
import type { UploadMapDefaultAction } from "domains/createMapDetail/actionTypes";
import {
  getCurrentCustomMapDescription,
  getCurrentCustomMapName,
  getCurrentMap,
  getExistingDimensionIds,
  getLocalImage,
  getNewCustomDimensions,
} from "domains/createMapDetail/selectors";
import { createCustomKeyIssueSuccess } from "domains/customKeyIssue/actions";
import { buildDimensionPost } from "domains/customKeyIssue/sagas/createCustomKeyIssueSaga";
import { pushModalScreen } from "domains/modal/actions";
import { handleCustomTopicUpdateFailureSaga } from "domains/topicDetail/sagas/handleCustomTopicUpdateFailureSaga";
import type { TopicVisibilityPatch } from "domains/topicshare/topicshare.types";
import { fetchFollowedTopics } from "entities/topics/actions";
import { getFollowedTopicsLimit, getFollowedTopicsOffset } from "entities/topics/selectors";
import type { TopicPost, TopicPut } from "entities/topics/types";
import { router } from "router/router";
import { store } from "store";

export function* uploadMapSaga(action: UploadMapDefaultAction) {
  const description: ReturnType<typeof getCurrentCustomMapDescription> = yield select(getCurrentCustomMapDescription);
  const name: ReturnType<typeof getCurrentCustomMapName> = yield select(getCurrentCustomMapName);
  const map: ReturnType<typeof getCurrentMap> = yield select(getCurrentMap);
  const dimensionIds: ReturnType<typeof getExistingDimensionIds> = yield select(getExistingDimensionIds);
  const followedTopicsLimit: ReturnType<typeof getFollowedTopicsLimit> = yield select(getFollowedTopicsLimit);
  const followedTopicsOffset: ReturnType<typeof getFollowedTopicsOffset> = yield select(getFollowedTopicsOffset);
  const image: ReturnType<typeof getLocalImage> = yield select(getLocalImage);
  const newCustomDimensions: ReturnType<typeof getNewCustomDimensions> = yield select(getNewCustomDimensions);

  const { closeEditor, pathname } = action.meta;

  try {
    let topic_id = "__UNSET__";

    if (map?.id === undefined) topic_id = yield postMap();
    else {
      yield putMap(map.id);
      topic_id = map.id;
    }

    const concurrentRequests = newCustomDimensions.map((dimension) =>
      apiFetch("dimension", {
        body: buildDimensionPost({ ...dimension, topic_ids: dimension.topics.map((topic) => topic.id) }, topic_id),
        method: "POST",
      }),
    );

    if (map?.id === undefined) {
      const topicVisibilityPatch: TopicVisibilityPatch = { visibility: map?.is_public ? "public" : "private" };
      concurrentRequests.push(
        apiFetch(`topic/${topic_id}/visibility`, { body: topicVisibilityPatch, method: "PATCH" }),
      );
    }

    if (image !== undefined) {
      const body = new FormData();
      body.append("image", dataUrlToBlob(image));
      concurrentRequests.push(apiFetch(`topic/${topic_id}/cover_image`, { body, method: "POST" }));
    }

    const responses: unknown[] = yield all(concurrentRequests);

    const images = (image === undefined ? map?.images : getImages(responses.at(-1))) ?? null;

    if (images === null) throw new Error("'images' is null in uploadMapSaga");

    yield put(createCustomKeyIssueSuccess());
    yield put(fetchFollowedTopics({ limit: followedTopicsLimit, offset: followedTopicsOffset }));
    yield put(topicsApiSlice.util.invalidateTags([{ type: "CustomMaps" }]));
    yield put(uploadMapSuccessful({ description, dimensionIds, id: topic_id, images, name }));

    yield trackSaga({
      entityId: topic_id,
      entityName: map?.name,
      name: map?.id ? "Edit Map" : "Create Map",
      page: "Create",
    });

    yield store.dispatch(topicsApiSlice.endpoints.getTopicMetadata.initiate(undefined, { forceRefetch: true }));

    if (closeEditor) yield router.navigate("/create");
    else if (map?.id === undefined) yield router.navigate(`/create/${topic_id}?editing=true`);
  } catch (error) {
    yield put(uploadMapFailure({ logoutOnAuthError: true }));
    if (!(error instanceof RequestError)) return;
    if (error.status === 403 || error.status === 404)
      yield handleCustomTopicUpdateFailureSaga({ errorStatus: error.status, pathname });
    else if (error.status !== 401) yield put(pushModalScreen({ id: "Error" }));
  }
}

function* postMap() {
  const description: ReturnType<typeof getCurrentCustomMapDescription> = yield select(getCurrentCustomMapDescription);
  const name: ReturnType<typeof getCurrentCustomMapName> = yield select(getCurrentCustomMapName);
  const map: ReturnType<typeof getCurrentMap> = yield select(getCurrentMap);
  const dimension_ids: ReturnType<typeof getExistingDimensionIds> = yield select(getExistingDimensionIds);

  const body: TopicPost = {
    invitations: { shared_users: map?.invitations ?? [] },
    topic: {
      description,
      dimension_ids,
      name,
      status: "Active",
      subtitle: null,
      type: "Custom Map",
    },
  };

  const response: { id: string } = yield apiFetch("topic", { body, method: "POST" });

  return response.id;
}

function* putMap(mapId: string) {
  const map: ReturnType<typeof getCurrentMap> = yield select(getCurrentMap);
  const description: ReturnType<typeof getCurrentCustomMapDescription> = yield select(getCurrentCustomMapDescription);
  const name: ReturnType<typeof getCurrentCustomMapName> = yield select(getCurrentCustomMapName);
  const dimension_ids: ReturnType<typeof getExistingDimensionIds> = yield select(getExistingDimensionIds);

  const body: TopicPut = {
    invitations: { shared_users: map?.invitations ?? [] },
    topic: {
      description,
      dimension_ids,
      id: mapId,
      name,
      status: "Active",
      subtitle: null,
      type: "Custom Map",
    },
  };

  yield apiFetch(`topic/${mapId}`, { body, method: "PUT" });
}

const getImages = (value: unknown): ImageCollection | undefined => {
  const images = isObject(value) && "images" in value ? value.images : undefined;
  return isImageCollection(images) ? images : undefined;
};

const isImageCollection = (value: unknown): value is ImageCollection =>
  isObject(value) &&
  requiredKeys.every((key) => key in value && typeof (value as Record<typeof key, unknown>)[key] === "string");

const requiredKeys = ["large", "medium", "x_small", "thumbnail"] as const;
